import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ACCEPTED_IMAGE = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
const ACCEPTED_IMAGE_MIME = 'image/jpeg,image/png,image/webp,image/gif';

const SAFETY_CHECKS = [
  { id: 'copyright', label: 'Copyright detection', icon: 'Copyright' },
  { id: 'watermark', label: 'Watermark scan', icon: 'Droplets' },
  { id: 'stock', label: 'Stock image identifier', icon: 'Search' },
  { id: 'content', label: 'Content safety filter', icon: 'Shield' },
];

export default function ImageUploadWithSafety({ onImageAccepted, disabled }) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [checkProgress, setCheckProgress] = useState(0);
  const [checkStep, setCheckStep] = useState(0);
  const [checkResult, setCheckResult] = useState(null); // 'safe' | 'rejected'
  const [rejectionReason, setRejectionReason] = useState('');

  const runSafetyCheck = (file) => {
    setIsChecking(true);
    setCheckProgress(0);
    setCheckStep(0);
    setCheckResult(null);

    let step = 0;
    const stepInterval = setInterval(() => {
      step++;
      setCheckStep(step);
      setCheckProgress((step / SAFETY_CHECKS?.length) * 100);
      if (step >= SAFETY_CHECKS?.length) {
        clearInterval(stepInterval);
        setTimeout(() => {
          setIsChecking(false);
          setCheckProgress(100);
          // Simulate safety check — in production this would call a real API
          const isSafe = !file?.name?.toLowerCase()?.includes('stock') &&
            !file?.name?.toLowerCase()?.includes('shutterstock') &&
            !file?.name?.toLowerCase()?.includes('getty');
          if (isSafe) {
            setCheckResult('safe');
            onImageAccepted?.(file);
          } else {
            setCheckResult('rejected');
            setRejectionReason('Uploaded image may contain copyrighted material. Please upload original or licensed images.');
            setUploadedImage(null);
          }
        }, 600);
      }
    }, 700);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const ext = '.' + file?.name?.split('.')?.pop()?.toLowerCase();
    if (!ACCEPTED_IMAGE?.includes(ext)) return;
    const url = URL.createObjectURL(file);
    setUploadedImage({ file, url });
    setCheckResult(null);
    runSafetyCheck(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    handleFileSelect(e?.dataTransfer?.files?.[0]);
  };

  const handleReset = () => {
    setUploadedImage(null);
    setCheckResult(null);
    setCheckProgress(0);
    setCheckStep(0);
    setIsChecking(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Icon name="ImagePlus" size={15} color="var(--color-primary)" />
        <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Upload Image for Video</p>
      </div>
      {!uploadedImage ? (
        <div
          onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef?.current?.click()}
          className="flex flex-col items-center justify-center gap-2 p-5 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200"
          style={{
            borderColor: dragOver ? 'var(--color-primary)' : 'var(--color-border)',
            background: dragOver ? 'rgba(99,102,241,0.06)' : 'var(--color-muted)',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.12)' }}>
            <Icon name="ImagePlus" size={20} color="var(--color-primary)" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}>Drop your image here</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>or click to browse</p>
          </div>
          <div className="flex gap-1.5 flex-wrap justify-center">
            {ACCEPTED_IMAGE?.map((ext) => (
              <span key={ext} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.1)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>{ext}</span>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden border" style={{ borderColor: checkResult === 'safe' ? 'rgba(16,185,129,0.4)' : checkResult === 'rejected' ? 'rgba(239,68,68,0.4)' : 'var(--color-border)' }}>
          <div className="relative h-32">
            <Image src={uploadedImage?.url} alt="Uploaded image for safety check" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.4)' }} />
            <button
              onClick={handleReset}
              className="absolute top-2 right-2 w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.6)' }}
            >
              <Icon name="X" size={13} color="white" />
            </button>
            {checkResult === 'safe' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(16,185,129,0.9)' }}>
                  <Icon name="ShieldCheck" size={16} color="white" />
                  <span className="text-sm font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>Image Approved</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <input ref={fileInputRef} type="file" accept={ACCEPTED_IMAGE_MIME} className="hidden" onChange={(e) => handleFileSelect(e?.target?.files?.[0])} disabled={disabled} />
      {/* Safety check progress */}
      {isChecking && (
        <div className="rounded-xl p-3 flex flex-col gap-2 animate-fade-in-up" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid var(--color-border)' }}>
          <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Running safety checks...</p>
          <div className="flex flex-col gap-1.5">
            {SAFETY_CHECKS?.map((check, idx) => (
              <div key={check?.id} className="flex items-center gap-2">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: idx < checkStep ? 'rgba(16,185,129,0.15)' : idx === checkStep ? 'rgba(99,102,241,0.15)' : 'var(--color-muted)' }}
                >
                  <Icon
                    name={idx < checkStep ? 'Check' : idx === checkStep ? 'Loader' : 'Circle'}
                    size={10}
                    color={idx < checkStep ? 'var(--color-success)' : idx === checkStep ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                  />
                </div>
                <span className="text-xs" style={{ color: idx <= checkStep ? 'var(--color-foreground)' : 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  {check?.label}
                </span>
              </div>
            ))}
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--color-muted)' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${checkProgress}%`, background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))' }} />
          </div>
        </div>
      )}
      {/* Result messages */}
      {checkResult === 'safe' && (
        <div className="flex items-center gap-2 p-3 rounded-xl animate-fade-in-up" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
          <Icon name="ShieldCheck" size={15} color="var(--color-success)" />
          <p className="text-xs font-medium" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}>Image passed all safety checks. Ready to use!</p>
        </div>
      )}
      {checkResult === 'rejected' && (
        <div className="flex items-start gap-2 p-3 rounded-xl animate-fade-in-up" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <Icon name="AlertTriangle" size={15} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
          <p className="text-xs" style={{ color: 'var(--color-error)', fontFamily: 'var(--font-caption)' }}>{rejectionReason}</p>
        </div>
      )}
    </div>
  );
}
